import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { ChartBar, ArrowsClockwise, Warning, UsersThree } from "@phosphor-icons/react";

// Per-phase engagement widget for /admin — parallel to the conversion funnel
// but unordered: it just answers "how many unique people set foot in each
// phase?" Backend endpoint returns one row per tracked phase event.
const RANGES = [
  { value: "7d",   label: "7 giorni" },
  { value: "30d",  label: "30 giorni" },
  { value: "12mo", label: "12 mesi" },
  { value: "all",  label: "Da sempre" },
];

export default function PlausibleEngagementWidget({ token }) {
  const [range, setRange] = useState("30d");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const base = process.env.REACT_APP_BACKEND_URL;
      const res = await axios.get(
        `${base}/api/admin/plausible-engagement?date_range=${range}`,
        { headers: { "X-Admin-Token": token } },
      );
      setData(res.data);
    } catch (e) {
      setError(e.response?.data?.detail || "Errore nel caricamento dell'engagement.");
    } finally {
      setLoading(false);
    }
  }, [range, token]);

  useEffect(() => { load(); }, [load]);

  const sortedPhases = (data?.phases || [])
    .slice()
    .sort((a, b) => b.visitors - a.visitors);
  const topVisitors = sortedPhases[0]?.visitors || 0;

  return (
    <div
      data-testid="admin-plausible-engagement"
      className="rounded-2xl border border-border bg-surface-card p-6 space-y-5"
    >
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 rounded-2xl bg-sage/15 flex items-center justify-center">
            <UsersThree size={20} weight="duotone" className="text-sage" />
          </div>
          <div>
            <h2 className="font-display font-black text-xl text-ink tracking-tight">
              Engagement per fase
            </h2>
            <p className="text-xs text-ink-soft font-sans mt-0.5">
              Visitatori unici per ciascuna fase · sito <span className="font-mono">{data?.site_id || "—"}</span>
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <select
            data-testid="engagement-range-select"
            value={range}
            onChange={(e) => setRange(e.target.value)}
            className="rounded-full border border-border bg-surface px-3 py-1.5 text-sm font-sans font-semibold text-ink focus:outline-none focus:ring-2 focus:ring-sage/40"
          >
            {RANGES.map(r => (
              <option key={r.value} value={r.value}>{r.label}</option>
            ))}
          </select>
          <button
            data-testid="engagement-reload"
            onClick={load}
            disabled={loading}
            aria-label="Ricarica"
            className="h-8 w-8 rounded-full border border-border bg-surface text-ink-soft hover:text-ink hover:bg-surface-alt transition-colors flex items-center justify-center disabled:opacity-50"
          >
            <ArrowsClockwise size={14} weight="bold" className={loading ? "animate-spin" : ""} />
          </button>
        </div>
      </div>

      {error && (
        <div data-testid="engagement-error" className="text-sm text-terracotta flex items-center gap-1.5 font-sans">
          <Warning size={14} weight="bold" /> {error}
        </div>
      )}

      {data?.error && (
        <div className="rounded-xl bg-terracotta/5 border border-terracotta/30 p-3 text-xs text-ink font-sans">
          Plausible ha risposto con un errore: <span className="font-mono">{data.error}</span>
        </div>
      )}

      {data && !data.configured && (
        <div className="rounded-xl bg-sand/30 border border-sand/50 p-3 text-xs text-ink font-sans">
          ⚠️ Plausible non configurato. Manca <span className="font-mono">PLAUSIBLE_API_KEY</span> o <span className="font-mono">PLAUSIBLE_SITE_ID</span> nel backend.
        </div>
      )}

      {data?.configured && topVisitors === 0 && !loading && (
        <div className="rounded-xl bg-sage/10 border border-sage/30 p-3 text-xs text-ink font-sans">
          Nessun evento registrato in questo periodo. Gli eventi vengono raccolti solo dal dominio <span className="font-mono">exchangeplan.app</span> e richiedono che i Goal siano attivi lato Plausible.
        </div>
      )}

      <div className="space-y-3">
        {sortedPhases.map((p) => {
          const widthPct = topVisitors ? (p.visitors / topVisitors) * 100 : 0;
          const slug = p.name.replace(/\s+/g, "-").toLowerCase();
          return (
            <div
              key={p.name}
              data-testid={`engagement-phase-${slug}`}
              className="space-y-1.5"
            >
              <div className="flex items-baseline justify-between gap-3">
                <div className="flex items-center gap-2 min-w-0">
                  <span className="font-sans font-semibold text-ink truncate">{p.label}</span>
                  <span className="text-[10px] uppercase tracking-widest font-mono text-ink-soft/70 truncate">
                    {p.name}
                  </span>
                </div>
                <div className="flex items-baseline gap-2 shrink-0">
                  <span
                    data-testid={`engagement-visitors-${slug}`}
                    className="font-display font-black text-2xl text-ink"
                  >
                    {p.visitors}
                  </span>
                  <span className="text-[11px] uppercase tracking-widest font-sans font-bold text-ink-soft">
                    visitatori
                  </span>
                </div>
              </div>
              <div className="h-2 bg-surface-alt rounded-full overflow-hidden">
                <div
                  className="h-full bg-sage transition-[width] duration-500 ease-out"
                  style={{ width: `${widthPct}%` }}
                />
              </div>
              <div className="flex items-center justify-between text-[11px] font-sans text-ink-soft">
                <span>{p.events} eventi totali</span>
                {p.share_of_top > 0 && (
                  <span className="font-mono">{p.share_of_top}% del top</span>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {data && (
        <div className="pt-2 border-t border-border/60 text-[11px] text-ink-soft font-sans flex items-center gap-1.5">
          <ChartBar size={12} weight="bold" />
          Periodo: {RANGES.find(r => r.value === data.date_range)?.label || data.date_range}
        </div>
      )}
    </div>
  );
}
